import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from './api';
import { shortDate } from './dates';
import { IconCopy, IconKey, IconPlus, IconTrash, IconUpload } from './Icons';
import { Modal } from './Modal';
import { copyText, TARGETS } from './share';
import { useToast } from './Toast';
import { AppUser, PaperInfo, ViewRow } from './types';

type Tab = 'users' | 'papers' | 'views' | 'account';

function CodeDialog({ user, onClose }: { user: AppUser; onClose: () => void }) {
  const toast = useToast();
  const msg = `Hello ${user.name}, your Royal Journalism access code is: ${user.code}`;
  return (
    <Modal title="Access code created" onClose={onClose}>
      <p>
        Send this code to <strong>{user.name}</strong>. You can copy it again later from the Users list (Copy code).
      </p>
      <div className="code-show">{user.code}</div>
      <div className="share-grid">
        <button
          className="chip strong"
          onClick={async () => toast((await copyText(user.code || '')) ? 'Code copied' : 'Could not copy', 'info')}
        >
          <IconCopy size={16} /> Copy code
        </button>
        {TARGETS.filter((t) => ['whatsapp', 'telegram', 'email', 'sms'].includes(t.id)).map((t) => (
          <a key={t.id} className={'chip ' + t.id} href={t.href(msg, '')} target="_blank" rel="noopener noreferrer">
            Send via {t.label}
          </a>
        ))}
      </div>
      <div className="actions">
        <button className="btn primary" onClick={onClose}>
          Done
        </button>
      </div>
    </Modal>
  );
}

/** Every shared page link and clipping that has been opened, most viewed first. */
function ViewsTable() {
  const [rows, setRows] = useState<ViewRow[] | null>(null);
  useEffect(() => {
    api.viewsOverview().then(setRows).catch(() => setRows([]));
  }, []);
  return (
    <div className="table-wrap">
      <table className="grid">
        <thead>
          <tr>
            <th>Shared</th>
            <th>Date · page</th>
            <th>Shared by</th>
            <th>Viewers</th>
            <th>Views</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r, i) => (
            <tr key={i}>
              <td>{r.kind === 'page' ? 'Page link' : 'Clipping'}</td>
              <td>
                {r.date} · page {r.page}
              </td>
              <td>{r.by}</td>
              <td>{r.viewers}</td>
              <td>{r.views}</td>
            </tr>
          ))}
          {rows && rows.length === 0 && (
            <tr>
              <td colSpan={5} className="muted">
                Nobody has opened a shared link yet. Counts start from the first time a link is opened (clippings are removed each midnight).
              </td>
            </tr>
          )}
          {rows === null && (
            <tr>
              <td colSpan={5} className="muted">
                Loading…
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function AdminPanel({ onPapersChanged, onSignOut }: { onPapersChanged: () => void; onSignOut?: () => void }) {
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('users');
  const [users, setUsers] = useState<AppUser[] | null>(null);
  const [papers, setPapers] = useState<PaperInfo[] | null>(null);
  const [newName, setNewName] = useState('');
  const [forgot, setForgot] = useState('');
  const [issued, setIssued] = useState<AppUser | null>(null);
  const [busy, setBusy] = useState(false);
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const replaceInput = useRef<HTMLInputElement>(null);
  const replaceDate = useRef<string>('');

  const fail = useCallback((e: unknown, fallback: string) => toast(e instanceof ApiError ? e.message : fallback, 'error'), [toast]);

  const loadUsers = useCallback(() => {
    api.users().then(setUsers).catch((e) => fail(e, 'Could not load users.'));
  }, [fail]);
  const loadPapers = useCallback(() => {
    api
      .all()
      .then((l) => setPapers([...l].sort((a, b) => b.date.localeCompare(a.date))))
      .catch((e) => fail(e, 'Could not load papers.'));
  }, [fail]);

  useEffect(() => {
    if (tab === 'users') loadUsers();
    if (tab === 'papers') loadPapers();
  }, [tab, loadUsers, loadPapers]);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await api.createUser(newName.trim());
      setIssued(u);
      setNewName('');
      loadUsers();
    } catch (x) {
      fail(x, 'Could not create the user.');
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async (u: AppUser) => {
    try {
      const { code } = await api.userCode(u.id);
      toast((await copyText(code)) ? `Code for ${u.name} copied` : 'Could not copy – use Reset code and copy it from the window', 'info');
    } catch (x) {
      fail(x, 'Could not get the code.');
    }
  };

  const reset = async (u: AppUser) => {
    if (!window.confirm(`Issue a new code for ${u.name}? Their old code will stop working.`)) return;
    try {
      setIssued(await api.resetUser(u.id));
      loadUsers();
    } catch (x) {
      fail(x, 'Could not reset the code.');
    }
  };

  const resetByName = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      setIssued(await api.resetByName(forgot.trim()));
      setForgot('');
      loadUsers();
    } catch (x) {
      fail(x, 'Could not reset the code.');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (u: AppUser) => {
    try {
      await api.setActive(u.id, !u.active);
      loadUsers();
    } catch (x) {
      fail(x, 'Could not update the user.');
    }
  };

  const del = async (u: AppUser) => {
    if (!window.confirm(`Remove ${u.name}? They will lose access.`)) return;
    try {
      await api.deleteUser(u.id);
      loadUsers();
    } catch (x) {
      fail(x, 'Could not remove the user.');
    }
  };

  const askReplace = (date: string) => {
    replaceDate.current = date;
    replaceInput.current?.click();
  };

  const doReplace = async (f: File | undefined) => {
    if (!f) return;
    const date = replaceDate.current;
    if (!window.confirm(`Replace the newspaper of ${shortDate(date)} with "${f.name}"?`)) return;
    toast('Uploading… please wait', 'info');
    try {
      await api.upload(date, f, true);
      toast('Newspaper replaced', 'ok');
      loadPapers();
      onPapersChanged();
    } catch (x) {
      fail(x, 'Replace failed.');
    }
  };

  const delPaper = async (p: PaperInfo) => {
    if (!window.confirm(`Delete the newspaper of ${shortDate(p.date)}? A new one can then be uploaded for that day.`)) return;
    try {
      await api.deletePaper(p.date);
      loadPapers();
      onPapersChanged();
    } catch (x) {
      fail(x, 'Could not delete.');
    }
  };

  const [keyPw, setKeyPw] = useState('');
  const [newKey, setNewKey] = useState('');
  const [hasKey, setHasKey] = useState<boolean | null>(null);
  useEffect(() => { if (tab === 'account') api.recoveryKeyExists().then((r) => setHasKey(r.exists)).catch(() => setHasKey(null)); }, [tab, newKey]);
  const makeKey = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const r = await api.makeRecoveryKey(keyPw);
      setNewKey(r.key);
      setKeyPw('');
    } catch (x) {
      fail(x, 'Could not make the recovery key.');
    }
  };

  const changePw = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.changePassword(oldPw, newPw);
      setOldPw('');
      setNewPw('');
      toast('Password changed. Please sign in again with the new password.', 'ok');
      // sign out, so the new password is the one that is tested at once
      if (onSignOut) setTimeout(onSignOut, 1200);
    } catch (x) {
      fail(x, 'Could not change the password.');
    }
  };

  return (
    <div className="page-pad">
      <h2 className="page-title">Admin</h2>
      <div className="tabs">
        {(['users', 'papers', 'views', 'account'] as Tab[]).map((t) => (
          <button key={t} className={tab === t ? 'on' : ''} onClick={() => setTab(t)}>
            {t === 'users' ? 'Users & codes' : t === 'papers' ? 'Newspapers' : t === 'views' ? 'Shared views' : 'My password'}
          </button>
        ))}
      </div>

      {tab === 'users' && (
        <>
          <form className="card inline-form" onSubmit={create}>
            <label htmlFor="nu">Add a user (actual full name)</label>
            <div className="row">
              <input id="nu" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Ravi Kumar" />
              <button className="btn primary" disabled={busy || newName.trim().length < 2}>
                <IconPlus size={16} /> Create code
              </button>
            </div>
          </form>
          <form className="card inline-form" onSubmit={resetByName}>
            <label htmlFor="fg">Forgot code? Type the user's actual name</label>
            <div className="row">
              <input id="fg" value={forgot} onChange={(e) => setForgot(e.target.value)} placeholder="Name exactly as registered" />
              <button className="btn" disabled={busy || forgot.trim().length < 2}>
                <IconKey size={16} /> Send new code
              </button>
            </div>
            <p className="muted small">A new 8-character code is made for that same user and the old one stops working.</p>
          </form>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Code issued</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users?.map((u) => (
                  <tr key={u.id}>
                    <td>{u.name}</td>
                    <td>
                      <span className={'pill ' + (u.active ? 'on' : 'off')}>{u.active ? 'Active' : 'Blocked'}</span>
                    </td>
                    <td>{shortDate(u.codeIssuedAt.slice(0, 10))}</td>
                    <td className="right">
                      <button className="btn small" onClick={() => copyCode(u)} title={u.hasCode ? 'Copy this user\'s code' : 'Press Reset code once to get a copyable code'} disabled={!u.hasCode}>
                        <IconCopy size={14} /> Copy code
                      </button>{' '}
                      <button className="btn small" onClick={() => reset(u)}>
                        Reset code
                      </button>{' '}
                      <button className="btn small" onClick={() => toggle(u)}>
                        {u.active ? 'Block' : 'Unblock'}
                      </button>{' '}
                      <button className="icon-btn danger" onClick={() => del(u)} aria-label={'Remove ' + u.name}>
                        <IconTrash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {users && users.length === 0 && (
                  <tr>
                    <td colSpan={4} className="muted">
                      No users yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'papers' && (
        <div className="table-wrap">
          <input ref={replaceInput} type="file" accept="application/pdf,.pdf" hidden onChange={(e) => {
            void doReplace(e.target.files?.[0]);
            e.target.value = '';
          }} />
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Pages</th>
                <th>Uploaded by</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {papers?.map((p) => (
                <tr key={p.date}>
                  <td>{shortDate(p.date)}</td>
                  <td>{p.pageCount}</td>
                  <td>{p.uploadedBy}</td>
                  <td className="right">
                    <button className="btn small" onClick={() => askReplace(p.date)}>
                      <IconUpload size={14} /> Replace PDF
                    </button>{' '}
                    <button className="icon-btn danger" onClick={() => delPaper(p)} aria-label="Delete paper">
                      <IconTrash size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {papers && papers.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">
                    No newspapers uploaded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'views' && <ViewsTable />}

      {tab === 'account' && (
        <form className="card inline-form" onSubmit={changePw}>
          <label htmlFor="op">Current password</label>
          <input id="op" type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} autoComplete="current-password" />
          <label htmlFor="np">New password (8+ characters)</label>
          <input id="np" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} autoComplete="new-password" />
          <button className="btn primary" disabled={!oldPw || newPw.length < 8}>
            Change password
          </button>
        </form>
      )}

      {tab === 'account' && (
        <form className="card inline-form" onSubmit={makeKey} style={{ marginTop: 12 }}>
          <h3 style={{ margin: '0 0 6px' }}>Recovery key (if you forget the password)</h3>
          <p className="hint">
            {hasKey ? 'A recovery key exists. Making a new one cancels the old one.' : 'No recovery key yet.'} Write the key down and keep it safe.
            On the sign-in screen, Admin → "Forgot password?" accepts your admin name + this key and lets you set a new password. The key works once.
          </p>
          {newKey && (
            <p role="status" style={{ fontSize: 22, fontWeight: 700, letterSpacing: 2, userSelect: 'all' }}>
              {newKey}
              <br />
              <span className="hint" style={{ fontSize: 13, fontWeight: 400, letterSpacing: 0 }}>Shown only now. Copy or write it down before you leave this page.</span>
            </p>
          )}
          <label htmlFor="kp">Your current password</label>
          <input id="kp" type="password" value={keyPw} onChange={(e) => setKeyPw(e.target.value)} autoComplete="current-password" />
          <button className="btn primary" disabled={!keyPw}>
            {hasKey ? 'Make a new recovery key' : 'Make recovery key'}
          </button>
        </form>
      )}

      {issued && <CodeDialog user={issued} onClose={() => setIssued(null)} />}
    </div>
  );
}
